/**
 * Shortage Override Request dialog — shown to MIA when Confirm returns
 * 409 shortage_requires_approval. Captures reason + note + optional photo and
 * submits to POST /pieces/{id}/break/request-override.
 */
import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Send, AlertTriangle, Camera, Trash2 } from 'lucide-react';
import { CATEGORY_LABEL } from './constants';

const REASONS = [
  { key: 'fell_during_cutting', label: 'Fell during cutting / pliers work' },
  { key: 'crushed_lost', label: 'Crushed and lost while breaking' },
  { key: 'missing_on_unpacking', label: 'Missing on unpacking' },
  { key: 'original_miscount_at_receipt', label: 'Original count wrong at receipt' },
  { key: 'damaged_beyond_recognition', label: 'Damaged beyond recognition' },
  { key: 'other', label: 'Other (explain in note)' },
];

const RequestOverrideDialog = ({
  open, onOpenChange,
  serial, shortage, // { diamond?:n, stone?:n, beads_pearl?:n }
  onSubmit, // async (payload) => void
  uploadPhoto, // async (file) => url
  submitting,
}) => {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);

  React.useEffect(() => {
    if (open) {
      setReason(''); setNote(''); setPhotoUrl('');
    }
  }, [open]);

  const handlePhoto = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    const url = await uploadPhoto(file);
    if (url) setPhotoUrl(url);
    setPhotoUploading(false);
  };

  const handleSubmit = () => {
    if (!reason) return;
    if (!note.trim() || note.trim().length < 6) return;
    onSubmit({ reason, note: note.trim(), photo_url: photoUrl || undefined });
  };

  const shortageEntries = Object.entries(shortage || {}).filter(([, v]) => v > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[95vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle className="flex items-center gap-2 text-rose-800">
            <AlertTriangle className="w-5 h-5" /> Shortage — Admin approval needed
          </DialogTitle>
          <DialogDescription>
            {serial} cannot be closed with a piece-count shortage. Explain what happened and send to an admin for approval.
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 pb-4 pt-2 space-y-4 overflow-y-auto flex-1">
          {/* Shortage strip */}
          <div className="grid grid-cols-3 gap-2" data-testid="override-shortage-strip">
            {['diamond', 'stone', 'beads_pearl'].map((cat) => {
              const s = (shortage || {})[cat] || 0;
              const tone = s > 0
                ? 'bg-rose-50 border-rose-200 text-rose-800'
                : 'bg-slate-50 border-slate-200 text-slate-500';
              return (
                <div key={cat} className={`rounded-lg border py-1.5 text-center ${tone}`}>
                  <div className="text-[10px] uppercase tracking-wide opacity-80">{CATEGORY_LABEL[cat]}</div>
                  <div className="text-sm font-semibold">
                    {s > 0 ? `${s} short` : 'OK'}
                  </div>
                </div>
              );
            })}
          </div>
          {shortageEntries.length === 0 && (
            <div className="text-center text-xs text-slate-500">
              No shortage — close this dialog and use Confirm instead.
            </div>
          )}

          <div>
            <Label className="text-xs">Reason</Label>
            <div className="space-y-1 mt-1" data-testid="override-reason-list">
              {REASONS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setReason(r.key)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    reason === r.key
                      ? 'bg-amber-700 text-white border-amber-800'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`override-reason-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Note to admin <span className="text-rose-600">*</span></Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What exactly happened? Where did you look? Any witnesses?"
              className="text-base"
              data-testid="override-note-input"
            />
            <div className="text-[10px] text-slate-500 mt-0.5">Minimum 6 characters — this goes into the audit log.</div>
          </div>

          <div>
            <Label className="text-xs">Photo evidence (recommended)</Label>
            {photoUrl ? (
              <div className="mt-1 relative w-full h-40 rounded-lg overflow-hidden border" data-testid="override-photo-preview">
                <img src={photoUrl} alt="evidence" className="w-full h-full object-cover" />
                <Button
                  size="icon" variant="destructive"
                  className="absolute top-2 right-2 h-8 w-8"
                  onClick={() => setPhotoUrl('')}
                  data-testid="override-photo-remove"
                  type="button"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <label className="mt-1 flex items-center justify-center gap-2 h-11 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/40 hover:bg-amber-50 text-amber-800 cursor-pointer text-sm">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handlePhoto(e.target.files?.[0])}
                  data-testid="override-photo-input"
                />
                {photoUploading ? 'Uploading…' : <><Camera className="w-4 h-4" /> Attach a photo</>}
              </label>
            )}
            <div className="text-[10px] text-slate-500 mt-0.5">Adding a photo speeds up admin approval.</div>
          </div>
        </div>

        <div className="border-t p-3 flex gap-2 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11 flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!reason || note.trim().length < 6 || submitting || photoUploading}
            className="h-11 flex-1 bg-rose-700 hover:bg-rose-800"
            data-testid="override-submit-btn"
          >
            <Send className="w-4 h-4 mr-1" /> {submitting ? 'Sending…' : 'Send for approval'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestOverrideDialog;
